// VIDE CONSTITUICAO_REGRAS.SH sec.2.5 | Art.182 184 (Dashboard Financeiro RSE)
// ============================================================================
// Recta Scripta Editora — Dashboard Financeiro (dentro do menu FINANCEIRO)
// ----------------------------------------------------------------------------
// Consome snapshot Bling em public/data/recta_bling_snapshot.json
// (gerado por scripts/python_tools/recta_scripta/gera_recta_bling_index.py).
//
// Cross-link com SSOT (recta_scripta_index.json) quando bling_proposta_id
// existir nos seeds locais.
// ============================================================================
import React, { useMemo, useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, AlertTriangle, Clock, FileText, Wallet,
  CheckCircle2, RefreshCw, ExternalLink, Search, Calendar, BookOpen,
  PieChart, Lock, Coins, Banknote
} from 'lucide-react';
import {
  useRectaBlingSnapshot,
  getKpis,
  getContasReceber,
  getContasPagar,
  getPropostasAbertas,
  getPedidos,
} from './useRectaBlingSnapshot';
import {
  PendenciasUrgentesPanel,
  useJsonPendencias,
  blingContasPagarToPendencyItems,
} from './_shared/FinanceSharedUI';
import { useRectaScriptaIndex } from '../recta_scripta/data/useRectaScriptaIndex';
import {
  computeSplitFromSnapshot,
  BUCKETS,
  BUCKET_LABELS,
  BUCKET_DESCRIPTIONS,
  SIMPLES_NACIONAL_DEFAULT_ALIQUOTA,
  RESERVA_PJ_MIN_PERCENT,
} from './SplitEngine';

const BRAND_BLUE = '#20243B';
const HIGHLIGHT_GOLD = '#C5A059';

const fmtBRL = (v) => {
  if (v == null || Number.isNaN(v)) return 'R$ 0,00';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    // FIX timezone: adicionar T12:00:00 evita shift UTC (bug dia anterior em UTC-3)
    const isoStr = String(iso);
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(isoStr);
    const d = isDateOnly ? new Date(isoStr + 'T12:00:00') : new Date(isoStr);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
};

export default function RectaFinanceDashboard() {
  const { data: snapshot, loading, error, refresh, refreshFromServer } = useRectaBlingSnapshot();
  const { data: ssot } = useRectaScriptaIndex();
  const [tab, setTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  // Auto-refresh na montagem: garante dados frescos ao navegar para a aba
  useEffect(() => { refresh(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshFromServer();
    } catch {
      refresh();
    } finally {
      setRefreshing(false);
    }
  };

  // Cross-link Bling → SSOT por bling_proposta_id
  const ssotPropostasByBlingId = useMemo(() => {
    const map = new Map();
    const list = ssot?.propostas || [];
    for (const p of list) {
      if (p.bling_proposta_id) map.set(String(p.bling_proposta_id), p);
    }
    return map;
  }, [ssot]);

  // Hook deve ficar antes de qualquer early return (Rules of Hooks)
  const jsonPendenciasRecta = useJsonPendencias({ escopo: 'recta' });

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: HIGHLIGHT_GOLD }} />
          <div className="text-sm font-bold text-slate-600">Carregando snapshot Bling…</div>
        </div>
      </div>
    );
  }

  // Erro / snapshot ausente
  if (error || !snapshot) {
    return (
      <div className="p-4">
        <SnapshotMissing error={error} />
      </div>
    );
  }

  const kpis = getKpis(snapshot);
  const counts = snapshot.stats?.counts || {};

  // Pendências unificadas: Bling contas a pagar + JSON canônico (Eloprint etc)
  const blingPendencias = blingContasPagarToPendencyItems(getContasPagar(snapshot));
  const pendencyItemsRecta = [...blingPendencias, ...jsonPendenciasRecta];

  return (
    <div className="space-y-4 pb-10">
      <PendenciasUrgentesPanel items={pendencyItemsRecta} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          label="A Receber"
          value={fmtBRL(kpis.total_a_receber)}
          icon={TrendingUp}
          accent="dark"
          subtitle={`${counts.contas_receber || 0} contas`}
        />
        <KpiCard
          label="Vencidos"
          value={fmtBRL(kpis.receber_vencidos)}
          icon={AlertTriangle}
          accent="red"
          subtitle={`${kpis.qtd_receber_vencidos} em atraso`}
        />
        <KpiCard
          label="A Pagar"
          value={fmtBRL(kpis.total_a_pagar)}
          icon={TrendingDown}
          accent="dark"
          subtitle={`${counts.contas_pagar || 0} contas`}
        />
        <KpiCard
          label="Saldo Projetado"
          value={fmtBRL(kpis.saldo_projetado)}
          icon={Wallet}
          accent={kpis.saldo_projetado >= 0 ? 'green' : 'red'}
          subtitle="Receber − Pagar"
        />
        <KpiCard
          label="Pipeline Propostas"
          value={fmtBRL(kpis.valor_pipeline_propostas)}
          icon={FileText}
          accent="gold"
          subtitle={`${kpis.qtd_propostas_abertas} em aberto`}
        />
        <KpiCard
          label="Faturamento 30d"
          value={fmtBRL(kpis.faturamento_30d)}
          icon={CheckCircle2}
          accent="green"
          subtitle={`${kpis.qtd_pedidos_30d} pedidos`}
        />
      </div>

      {/* Tabs + Botão Refresh */}
      <div className="border-b border-slate-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1 overflow-x-auto flex-1">
          <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>Visão Geral</TabButton>
          <TabButton active={tab === 'receber'} onClick={() => setTab('receber')} count={(counts.contas_receber || 0)}>A Receber</TabButton>
          <TabButton active={tab === 'pagar'} onClick={() => setTab('pagar')} count={(counts.contas_pagar || 0)}>A Pagar</TabButton>
          <TabButton active={tab === 'propostas'} onClick={() => setTab('propostas')} count={kpis.qtd_propostas_abertas}>Propostas Bling</TabButton>
          <TabButton active={tab === 'faturamento'} onClick={() => setTab('faturamento')}>Faturamento</TabButton>
          <TabButton active={tab === 'split'} onClick={() => setTab('split')}>Split</TabButton>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            title="Atualizar dados do Bling"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-50 hover:border-[#C5A059] hover:text-[#20243B] disabled:opacity-40 transition-all flex-shrink-0 mr-1 mb-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${(loading || refreshing) ? 'animate-spin text-[#C5A059]' : ''}`} />
            {loading || refreshing ? 'Buscando no Bling…' : 'Atualizar Bling'}
          </button>
        </div>
      </div>

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab kpis={kpis} snapshot={snapshot} />}
      {tab === 'receber' && <ContasTable items={getContasReceber(snapshot)} kind="receber" />}
      {tab === 'pagar' && <ContasTable items={getContasPagar(snapshot)} kind="pagar" />}
      {tab === 'propostas' && (
        <PropostasTable
          items={getPropostasAbertas(snapshot)}
          ssotPropostasByBlingId={ssotPropostasByBlingId}
        />
      )}
      {tab === 'faturamento' && <PedidosTable items={getPedidos(snapshot)} />}
      {tab === 'split' && <SplitTab snapshot={snapshot} />}

      {/* Footer */}
      <div className="text-[10px] text-slate-500 border-t border-slate-200 pt-3 flex items-center justify-between">
        <div>
          Snapshot Bling: <code>{snapshot.snapshot_dir}</code>
          {snapshot.snapshot_generated_at ? (
            <span className="ml-2">· extraído em {fmtDate(snapshot.snapshot_generated_at)}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {snapshot?.source === 'render-live'
            ? <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">✅ Dados ao vivo (Render)</span>
            : <span className="text-amber-600">⚠️ Snapshot local — clique <b>Atualizar Bling</b> para dados frescos</span>}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function DashboardHeader({ snapshot }) {
  return (
    <div
      className="rounded-xl p-5 text-white relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #2d3454 100%)`,
        borderLeft: `4px solid ${HIGHLIGHT_GOLD}`,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4" style={{ color: HIGHLIGHT_GOLD }} />
            <span
              className="text-[9px] font-bold uppercase tracking-widest"
              style={{ color: HIGHLIGHT_GOLD }}
            >
              📊 Dashboard Financeiro — Recta Scripta Editora
            </span>
          </div>
          <h2 className="text-xl font-bold mb-0.5">
            Gestão Financeira da Gráfica/Editora
          </h2>
          <div className="text-[11px] text-white/70">
            Tempo real (snapshot Bling) · Contas, propostas, faturamento e fluxo de caixa
          </div>
        </div>
        {snapshot ? (
          <span
            className="px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider flex-shrink-0"
            style={{ backgroundColor: HIGHLIGHT_GOLD, color: BRAND_BLUE }}
          >
            Bling Live
          </span>
        ) : null}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent, subtitle }) {
  const styles = {
    dark: { bg: BRAND_BLUE, fg: '#FFFFFF', accent: HIGHLIGHT_GOLD, border: 'transparent' },
    gold: { bg: '#FAF7F0', fg: BRAND_BLUE, accent: HIGHLIGHT_GOLD, border: HIGHLIGHT_GOLD },
    green: { bg: '#F0FDF4', fg: '#14532D', accent: '#22C55E', border: '#86EFAC' },
    red: { bg: '#FEF2F2', fg: '#7F1D1D', accent: '#EF4444', border: '#FCA5A5' },
  };
  const s = styles[accent] || styles.dark;

  return (
    <div
      className="rounded-xl p-3 border-2 shadow-sm"
      style={{ backgroundColor: s.bg, color: s.fg, borderColor: s.border }}
    >
      <div className="flex items-start justify-between mb-1">
        <span
          className="text-[9px] font-bold uppercase tracking-wider opacity-80"
        >
          {label}
        </span>
        {Icon ? <Icon className="w-3.5 h-3.5" style={{ color: s.accent }} /> : null}
      </div>
      <div className="text-lg sm:text-xl font-bold leading-tight tabular-nums" style={{ color: s.accent }}>
        {value}
      </div>
      {subtitle ? (
        <div className="text-[9px] opacity-70 mt-0.5">{subtitle}</div>
      ) : null}
    </div>
  );
}

function TabButton({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative px-3 sm:px-4 py-2 text-[12px] font-bold uppercase tracking-wider
        whitespace-nowrap transition-colors flex items-center gap-1.5
        ${active ? 'text-[#20243B]' : 'text-slate-500 hover:text-slate-700'}
      `}
    >
      {children}
      {count != null ? (
        <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-100 text-slate-600">
          {count}
        </span>
      ) : null}
      {active ? (
        <span
          className="absolute bottom-0 left-0 right-0 h-0.5"
          style={{ backgroundColor: HIGHLIGHT_GOLD }}
        />
      ) : null}
    </button>
  );
}

function OverviewTab({ kpis, snapshot }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Panel title="Próximos 7 dias">
        <Row label="A receber em 7 dias" value={fmtBRL(kpis.receber_proximos_7d)} positive />
        <Row label="A pagar em 7 dias" value={fmtBRL(kpis.pagar_proximos_7d)} negative />
        <Row label="Líquido projetado 7d" value={fmtBRL(kpis.receber_proximos_7d - kpis.pagar_proximos_7d)} bold />
      </Panel>

      <Panel title="Próximos 30 dias">
        <Row label="A receber em 30 dias" value={fmtBRL(kpis.receber_proximos_30d)} positive />
        <Row label="Faturamento últimos 30d" value={fmtBRL(kpis.faturamento_30d)} positive bold />
        <Row label="Pipeline propostas" value={fmtBRL(kpis.valor_pipeline_propostas)} muted />
      </Panel>

      <Panel title="Atrasos (ação necessária)" emphasis="red">
        <Row label="A receber vencido" value={fmtBRL(kpis.receber_vencidos)} negative bold />
        <Row label="Qtd contas em atraso" value={`${kpis.qtd_receber_vencidos} contas`} muted />
        <Row label="A pagar vencido" value={fmtBRL(kpis.pagar_vencidos)} negative />
        <Row label="Qtd contas a pagar atrasadas" value={`${kpis.qtd_pagar_vencidos} contas`} muted />
      </Panel>

      <Panel title="Volume operacional">
        <Row label="Total contas a receber" value={`${kpis.qtd_contas_receber_total}`} muted />
        <Row label="Total contas a pagar" value={`${kpis.qtd_contas_pagar_total}`} muted />
        <Row label="Pedidos últimos 30d" value={`${kpis.qtd_pedidos_30d}`} muted />
        <Row label="Propostas abertas" value={`${kpis.qtd_propostas_abertas}`} muted />
      </Panel>
    </div>
  );
}

function Panel({ title, emphasis, children }) {
  const borderColor = emphasis === 'red' ? '#FCA5A5' : '#E2E8F0';
  return (
    <div
      className="rounded-xl bg-white border p-4"
      style={{ borderColor }}
    >
      <h3 className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-3 pb-2 border-b border-slate-100">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value, positive, negative, bold, muted }) {
  let color = 'text-slate-700';
  if (positive) color = 'text-emerald-700';
  if (negative) color = 'text-red-700';
  if (muted) color = 'text-slate-500';
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-slate-600">{label}</span>
      <span className={`tabular-nums ${color} ${bold ? 'font-bold' : 'font-medium'}`}>
        {value}
      </span>
    </div>
  );
}

function ContasTable({ items, kind }) {
  const [search, setSearch] = useState('');
  const [showVencidosOnly, setShowVencidosOnly] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (showVencidosOnly) {
        const venc = c.dataVencimento || c.vencimento;
        if (!venc || venc >= today) return false;
      }
      if (q) {
        const hay = JSON.stringify(c).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, showVencidosOnly, today]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a.dataVencimento || a.vencimento || '';
      const vb = b.dataVencimento || b.vencimento || '';
      return String(va).localeCompare(String(vb));
    });
  }, [filtered]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar contato, histórico, número…"
            className="w-full pl-9 pr-3 h-9 rounded-lg border border-slate-300 bg-white text-sm
                       focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40"
          />
        </div>
        <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showVencidosOnly}
            onChange={(e) => setShowVencidosOnly(e.target.checked)}
            className="accent-red-600"
          />
          Apenas vencidos
        </label>
        <span className="text-[11px] text-slate-500 ml-auto">
          <strong className="text-slate-800">{sorted.length}</strong> {kind === 'receber' ? 'a receber' : 'a pagar'}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase font-bold tracking-wider text-slate-500">
              <th className="px-3 py-2.5 w-24">Vencimento</th>
              <th className="px-3 py-2.5">Contato</th>
              <th className="px-3 py-2.5">Histórico</th>
              <th className="px-3 py-2.5 w-28 text-right">Valor</th>
              <th className="px-3 py-2.5 w-20">Situação</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-12 text-center text-slate-400">
                  Nenhuma conta {kind === 'receber' ? 'a receber' : 'a pagar'} {showVencidosOnly ? 'vencida' : ''}.
                </td>
              </tr>
            ) : sorted.map((c, i) => {
              const venc = c.dataVencimento || c.vencimento;
              const valor = parseMoney(c.valor || c.valorTotal);
              const vencido = venc && venc < today;
              const sit = c.situacao;
              const sitId = (typeof sit === 'object' && sit) ? (sit.id || sit.valor) : sit;
              const baixado = String(sitId) === '2';

              return (
                <tr
                  key={c.id || i}
                  className="border-b border-slate-100 hover:bg-amber-50/30"
                >
                  <td className="px-3 py-2 tabular-nums">
                    <span className={vencido ? 'text-red-700 font-bold' : 'text-slate-700'}>
                      {fmtDate(venc)}
                    </span>
                    {vencido ? <span className="text-[9px] text-red-600 ml-1">●</span> : null}
                  </td>
                  <td className="px-3 py-2 truncate max-w-xs">
                    {(c.contato && c.contato.nome) || '—'}
                  </td>
                  <td className="px-3 py-2 truncate max-w-md text-slate-600">
                    {c.historico || c.descricao || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">
                    {fmtBRL(valor)}
                  </td>
                  <td className="px-3 py-2">
                    {baixado ? (
                      <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-100 text-emerald-800 font-bold uppercase">Baixada</span>
                    ) : vencido ? (
                      <span className="px-1.5 py-0.5 rounded text-[9px] bg-red-100 text-red-800 font-bold uppercase">Vencida</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-100 text-blue-800 font-bold uppercase">Aberta</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PropostasTable({ items, ssotPropostasByBlingId }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => JSON.stringify(p).toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar proposta…"
            className="w-full pl-9 pr-3 h-9 rounded-lg border border-slate-300 bg-white text-sm
                       focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40"
          />
        </div>
        <span className="text-[11px] text-slate-500">
          <strong className="text-slate-800">{filtered.length}</strong> em aberto
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase font-bold tracking-wider text-slate-500">
              <th className="px-3 py-2.5 w-16">Nº</th>
              <th className="px-3 py-2.5 w-28">Data</th>
              <th className="px-3 py-2.5">Contato</th>
              <th className="px-3 py-2.5 w-32 text-right">Total</th>
              <th className="px-3 py-2.5 w-24">SSOT</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-12 text-center text-slate-400">
                  Nenhuma proposta em aberto.
                </td>
              </tr>
            ) : filtered.map((p) => {
              const ssotMatch = ssotPropostasByBlingId.get(String(p.id));
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-amber-50/30">
                  <td className="px-3 py-2 font-mono font-bold text-slate-800">{p.numero || '?'}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{fmtDate(p.data)}</td>
                  <td className="px-3 py-2 truncate max-w-md">
                    {(p.contato && p.contato.nome) || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">
                    {fmtBRL(parseMoney(p.total))}
                  </td>
                  <td className="px-3 py-2">
                    {ssotMatch ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                        style={{ backgroundColor: HIGHLIGHT_GOLD, color: BRAND_BLUE }}
                        title={`Linkado: ${ssotMatch.id}`}
                      >
                        ✓ ligada
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PedidosTable({ items }) {
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => String(b.data || '').localeCompare(String(a.data || '')));
  }, [items]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 text-[11px] text-slate-600">
        <strong>{sorted.length}</strong> pedidos · ordenados por data desc
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase font-bold tracking-wider text-slate-500">
              <th className="px-3 py-2.5 w-16">Nº</th>
              <th className="px-3 py-2.5 w-24">Data</th>
              <th className="px-3 py-2.5">Contato</th>
              <th className="px-3 py-2.5 w-28 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, 100).map((p) => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono font-bold text-slate-800">{p.numero || '?'}</td>
                <td className="px-3 py-2 tabular-nums text-slate-600">{fmtDate(p.data)}</td>
                <td className="px-3 py-2 truncate max-w-md">
                  {(p.contato && p.contato.nome) || '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-bold">
                  {fmtBRL(parseMoney(p.total))}
                </td>
              </tr>
            ))}
            {sorted.length > 100 ? (
              <tr>
                <td colSpan={4} className="px-3 py-2 text-center text-[10px] text-slate-400">
                  Mostrando 100 de {sorted.length} pedidos.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SnapshotMissing({ error }) {
  const isNotFound = error?.code === 'SNAPSHOT_NOT_FOUND';
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-6 mt-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-amber-700 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-base font-bold text-amber-900 mb-2">
            {isNotFound ? 'Snapshot Bling ainda não foi gerado' : 'Erro ao carregar snapshot Bling'}
          </h3>
          <p className="text-[12px] text-amber-800 mb-3">
            {isNotFound
              ? 'O arquivo public/data/recta_bling_snapshot.json não existe. Para gerar:'
              : `Erro: ${String(error?.message || error)}`}
          </p>
          <div className="rounded-lg bg-white border border-amber-200 p-3 font-mono text-[11px] text-slate-700 space-y-1.5">
            <div>
              <span className="text-slate-400"># 1. Extrair snapshot do Bling (auth OAuth):</span>
            </div>
            <div className="bg-slate-100 px-2 py-1 rounded">
              cd scripts\python_tools<br/>
              python -m recta_bling.extrai_snapshot
            </div>
            <div className="mt-2">
              <span className="text-slate-400"># 2. Consolidar para o SPA:</span>
            </div>
            <div className="bg-slate-100 px-2 py-1 rounded">
              cd ..\..<br/>
              python scripts\python_tools\recta_scripta\gera_recta_bling_index.py --minify
            </div>
          </div>
          <div className="mt-3 text-[10px] text-amber-700">
            Pré-requisito: ~/.bling_app.env (CLIENT_ID/SECRET) e ~/.bling_creds.json (tokens OAuth) válidos.
          </div>
        </div>
      </div>
    </div>
  );
}

// Helpers
function parseMoney(v) {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace('R$', '').replace(/\s/g, '').trim();
  if (s.includes(',') && s.lastIndexOf(',') > s.lastIndexOf('.')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  }
  return parseFloat(s) || 0;
}

// ============================================================================
// SplitTab — visualizacao dos 4 buckets do Art. 182 (Constituicao RDA V24.0)
// ----------------------------------------------------------------------------
// Calculo logico apenas. Nao executa pagamentos.
// Cláusula petrea (Art. 181-IV): bucket3 jamais e proposto para conta PF.
// ============================================================================
function SplitTab({ snapshot }) {
  const kpis = snapshot?.kpis || {};
  const defaultReceita = kpis.faturamento_30d || 0;
  const defaultCustos = kpis.total_a_pagar || 0;

  const [receita, setReceita] = useState(defaultReceita);
  const [custos, setCustos] = useState(defaultCustos);
  const [aliquota, setAliquota] = useState(SIMPLES_NACIONAL_DEFAULT_ALIQUOTA);
  const [percentPJ, setPercentPJ] = useState(RESERVA_PJ_MIN_PERCENT);

  const split = useMemo(
    () =>
      computeSplitFromSnapshot(snapshot, {
        receitaBrutaOverride: receita,
        custosDiretosOverride: custos,
        aliquotaImposto: aliquota,
        percentReservaPJ: percentPJ,
      }),
    [snapshot, receita, custos, aliquota, percentPJ],
  );

  const cards = [
    {
      key: BUCKETS.CUSTOS_DIRETOS,
      icon: Banknote,
      accent: { bg: '#FEF3C7', fg: '#78350F', accent: '#D97706' },
      value: split[BUCKETS.CUSTOS_DIRETOS],
    },
    {
      key: BUCKETS.RESERVA_IMPOSTO,
      icon: Coins,
      accent: { bg: '#FEE2E2', fg: '#7F1D1D', accent: '#DC2626' },
      value: split[BUCKETS.RESERVA_IMPOSTO],
    },
    {
      key: BUCKETS.RESERVA_EMERGENCIA_PJ,
      icon: Lock,
      accent: { bg: '#DBEAFE', fg: '#1E3A8A', accent: '#2563EB' },
      value: split[BUCKETS.RESERVA_EMERGENCIA_PJ],
    },
    {
      key: BUCKETS.CAIXA_LIVRE,
      icon: Wallet,
      accent: { bg: '#DCFCE7', fg: '#14532D', accent: '#16A34A' },
      value: split[BUCKETS.CAIXA_LIVRE],
    },
  ];

  return (
    <div className="mt-4 space-y-4">
      {/* Cabecalho */}
      <div
        className="rounded-xl p-4 border-2 flex items-start gap-3"
        style={{ backgroundColor: '#FAF7F0', borderColor: HIGHLIGHT_GOLD }}
      >
        <PieChart className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: HIGHLIGHT_GOLD }} />
        <div className="text-[12px] text-slate-700 leading-relaxed">
          <strong style={{ color: BRAND_BLUE }}>Split Lógico de Receitas (Art. 182)</strong> —
          decomposição matemática automática de toda receita PJ em quatro buckets.
          Este painel é <strong>somente leitura/cálculo</strong>: nenhum valor é movimentado.
          Pagamentos a fornecedores são feitos manualmente pelo titular no app C6 PJ.
        </div>
      </div>

      {/* Inputs editaveis */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SplitInput
          label="Receita bruta (R$)"
          value={receita}
          onChange={setReceita}
          step={100}
          hint="Default: faturamento 30d (snapshot Bling)"
        />
        <SplitInput
          label="Custos diretos (R$)"
          value={custos}
          onChange={setCustos}
          step={100}
          hint="Proxy: total a pagar PJ"
        />
        <SplitInput
          label="Alíquota DAS Simples (%)"
          value={aliquota * 100}
          onChange={(v) => setAliquota(Math.max(0, v) / 100)}
          step={0.5}
          hint="Calibrar com contador"
        />
        <SplitInput
          label="Reserva PJ (%) — mín. 20%"
          value={percentPJ * 100}
          onChange={(v) => setPercentPJ(Math.max(RESERVA_PJ_MIN_PERCENT * 100, v) / 100)}
          step={1}
          hint="Art. 181-I (mínimo absoluto)"
        />
      </div>

      {/* 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(({ key, icon: Icon, accent, value }) => {
          const pct = split.receitaBruta > 0 ? value / split.receitaBruta : 0;
          return (
            <div
              key={key}
              className="rounded-xl p-4 border-2 shadow-sm"
              style={{ backgroundColor: accent.bg, borderColor: accent.accent }}
            >
              <div className="flex items-start justify-between mb-2">
                <span
                  className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: accent.fg }}
                >
                  {BUCKET_LABELS[key]}
                </span>
                <Icon className="w-4 h-4" style={{ color: accent.accent }} />
              </div>
              <div
                className="text-xl font-bold leading-tight tabular-nums"
                style={{ color: accent.accent }}
              >
                {fmtBRL(value)}
              </div>
              <div className="text-[10px] mt-1 opacity-80" style={{ color: accent.fg }}>
                {(pct * 100).toFixed(1)}% da receita bruta
              </div>
              {/* barra de progresso */}
              <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, pct * 100)}%`,
                    backgroundColor: accent.accent,
                  }}
                />
              </div>
              <div
                className="text-[10px] mt-2 leading-snug"
                style={{ color: accent.fg, opacity: 0.85 }}
              >
                {BUCKET_DESCRIPTIONS[key]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Totais auxiliares */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          Resumo do cálculo
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
          <SplitTotal label="Receita bruta" value={split.receitaBruta} />
          <SplitTotal label="(−) Custos diretos" value={-split[BUCKETS.CUSTOS_DIRETOS]} />
          <SplitTotal label="(−) Reserva imposto" value={-split[BUCKETS.RESERVA_IMPOSTO]} />
          <SplitTotal label="= Lucro líquido" value={split.lucroLiquido} bold />
        </div>
        <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">
          Lucro líquido × {(percentPJ * 100).toFixed(0)}% = {fmtBRL(split[BUCKETS.RESERVA_EMERGENCIA_PJ])} retidos
          em Reserva de Emergência PJ. Restante ({fmtBRL(split[BUCKETS.CAIXA_LIVRE])}) compõe o Caixa Livre,
          de onde sai o pró-labore de subsistência (Art. 181-V) e contas operacionais PJ.
        </div>
      </div>
    </div>
  );
}

function SplitInput({ label, value, onChange, step = 1, hint }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
        {label}
      </span>
      <input
        type="number"
        step={step}
        min={0}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full px-2 py-1.5 rounded border-2 border-slate-200 focus:border-[#C5A059] focus:outline-none text-[13px] tabular-nums"
      />
      {hint ? <span className="block text-[9px] text-slate-400 mt-0.5">{hint}</span> : null}
    </label>
  );
}

function SplitTotal({ label, value, bold }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={`tabular-nums ${bold ? 'font-bold text-base' : 'text-sm'}`}
        style={{ color: bold ? BRAND_BLUE : '#334155' }}
      >
        {fmtBRL(value)}
      </div>
    </div>
  );
}
